// WEEKEND WORK APPROVALS - Manager Screen
// Allows managers to approve/reject weekend work requests from their team.
// Phase 8: Weekend Work Approval

import React, { useState, useEffect } from 'react'
import { Row, Col, Card, Button, Modal, Form, Badge, Tab, Tabs } from 'react-bootstrap'
import { format, parseISO } from 'date-fns'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'
import { useAuth } from '../../modules/auth'
import {
  getPendingWeekendWorkRequests,
  approveWeekendWorkRequest,
  rejectWeekendWorkRequest,
  WeekendWorkRequest as WeekendWorkRequestType,
  WeekendWorkApproveInput,
  WeekendWorkRejectInput,
} from '../../../api/WeekendWorkAPI'

const WeekendWorkApprovals = () => {
  const { currentUser } = useAuth()
  const managerId = currentUser?.id || ''

  // State
  const [requests, setRequests] = useState<WeekendWorkRequestType[]>([])
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('pending')

  // Modal states
  const [showApproveModal, setShowApproveModal] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [selectedRequest, setSelectedRequest] = useState<WeekendWorkRequestType | null>(null)

  // Approve form
  const [approveData, setApproveData] = useState<Omit<WeekendWorkApproveInput, 'reviewedById'>>({
    approvalRemarks: '',
    compOffGranted: false,
    compOffHours: 0,
    compOffReason: '',
  })

  // Reject form
  const [rejectReason, setRejectReason] = useState('')

  // Load requests on mount
  useEffect(() => {
    loadRequests()
  }, [managerId])

  const loadRequests = async () => {
    if (!managerId) return
    setLoading(true)
    try {
      // const response = await getPendingWeekendWorkRequests({managerId})
      // setRequests(response.data.data?.entries || [])
      const response = await getPendingWeekendWorkRequests({ managerId })
      setRequests(response.data.data?.requests || [])
    } catch (error: any) {
      toast.error(error.message || 'Failed to load requests')
    } finally {
      setLoading(false)
    }
  }

  const openApproveModal = (request: WeekendWorkRequestType) => {
    setSelectedRequest(request)
    setApproveData({
      approvalRemarks: '',
      compOffGranted: false,
      compOffHours: 0,
      compOffReason: '',
    })
    setShowApproveModal(true)
  }

  const openRejectModal = (request: WeekendWorkRequestType) => {
    setSelectedRequest(request)
    setRejectReason('')
    setShowRejectModal(true)
  }

  const handleApprove = async () => {
    if (!selectedRequest) return

    try {
      await approveWeekendWorkRequest(selectedRequest.id, {
        reviewedById: managerId,
        ...approveData,
      })
      toast.success('Weekend work request approved successfully!')
      setShowApproveModal(false)
      loadRequests()
    } catch (error: any) {
      toast.error(error.message || 'Failed to approve request')
    }
  }

  const handleReject = async () => {
    if (!selectedRequest) return

    if (!rejectReason || rejectReason.trim().length < 10) {
      toast.error('Please provide a detailed reason for rejection (at least 10 characters)')
      return
    }

    try {
      await rejectWeekendWorkRequest(selectedRequest.id, {
        reviewedById: managerId,
        rejectionReason: rejectReason,
      })
      toast.success('Weekend work request rejected')
      setShowRejectModal(false)
      loadRequests()
    } catch (error: any) {
      toast.error(error.message || 'Failed to reject request')
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'warning'
      case 'APPROVED':
        return 'success'
      case 'REJECTED':
        return 'danger'
      case 'CANCELLED':
        return 'secondary'
      default:
        return 'secondary'
    }
  }

  const filteredRequests = () => {
    switch (activeTab) {
      case 'pending':
        return requests.filter((r) => r.status === 'PENDING')
      case 'approved':
        return requests.filter((r) => r.status === 'APPROVED')
      case 'rejected':
        return requests.filter((r) => r.status === 'REJECTED')
      case 'all':
      default:
        return requests
    }
  }

  const displayedRequests = filteredRequests()

  return (
    <>
      {/* Page Header */}
      <Row className='mb-4'>
        <Col>
          <h2 className='fw-bold mb-1'>Weekend Work Approvals</h2>
          <p className='text-muted mb-0'>Review and approve weekend work requests from your team</p>
        </Col>
      </Row>

      {/* Summary Cards */}
      <Row className='mb-4'>
        <Col md={3}>
          <Card className='border-warning'>
            <Card.Body>
              <div className='d-flex align-items-center'>
                <div className='fs-2 text-warning me-3'>
                  <i className='bi bi-clock-history'></i>
                </div>
                <div>
                  <div className='text-muted small'>Pending Approval</div>
                  <div className='fs-4 fw-bold'>
                    {requests.filter((r) => r.status === 'PENDING').length}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className='border-success'>
            <Card.Body>
              <div className='d-flex align-items-center'>
                <div className='fs-2 text-success me-3'>
                  <i className='bi bi-check-circle'></i>
                </div>
                <div>
                  <div className='text-muted small'>Approved</div>
                  <div className='fs-4 fw-bold'>
                    {requests.filter((r) => r.status === 'APPROVED').length}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className='border-danger'>
            <Card.Body>
              <div className='d-flex align-items-center'>
                <div className='fs-2 text-danger me-3'>
                  <i className='bi bi-x-circle'></i>
                </div>
                <div>
                  <div className='text-muted small'>Rejected</div>
                  <div className='fs-4 fw-bold'>
                    {requests.filter((r) => r.status === 'REJECTED').length}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className='border-info'>
            <Card.Body>
              <div className='d-flex align-items-center'>
                <div className='fs-2 text-info me-3'>
                  <i className='bi bi-calendar-event'></i>
                </div>
                <div>
                  <div className='text-muted small'>Total Requests</div>
                  <div className='fs-4 fw-bold'>{requests.length}</div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Requests List with Tabs */}
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'pending')}>
                <Tab
                  eventKey='pending'
                  title={
                    <span>
                      Pending{' '}
                      <Badge bg='warning' text='dark'>
                        {requests.filter((r) => r.status === 'PENDING').length}
                      </Badge>
                    </span>
                  }
                />
                <Tab
                  eventKey='approved'
                  title={
                    <span>
                      Approved{' '}
                      <Badge bg='success'>
                        {requests.filter((r) => r.status === 'APPROVED').length}
                      </Badge>
                    </span>
                  }
                />
                <Tab
                  eventKey='rejected'
                  title={
                    <span>
                      Rejected{' '}
                      <Badge bg='danger'>
                        {requests.filter((r) => r.status === 'REJECTED').length}
                      </Badge>
                    </span>
                  }
                />
                <Tab eventKey='all' title='All' />
              </Tabs>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className='text-center py-5'>
                  <div className='spinner-border text-primary' role='status'>
                    <span className='visually-hidden'>Loading...</span>
                  </div>
                </div>
              ) : displayedRequests.length === 0 ? (
                <div className='text-center py-5'>
                  <i className='bi bi-inbox fs-1 text-muted'></i>
                  <p className='text-muted mt-3'>
                    No {activeTab === 'all' ? '' : activeTab} requests found
                  </p>
                </div>
              ) : (
                <div className='table-responsive'>
                  <table className='table table-hover'>
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Date</th>
                        <th>Reason</th>
                        <th>Hours</th>
                        <th>Project</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedRequests.map((request) => (
                        <tr key={request.id}>
                          <td>
                            <strong>
                              {request.user?.firstName} {request.user?.lastName}
                            </strong>
                            <br />
                            <small className='text-muted'>{request.user?.email}</small>
                          </td>
                          <td>
                            <strong>{format(parseISO(request.requestDate), 'MMM dd, yyyy')}</strong>
                            <br />
                            <small className='text-muted'>
                              {format(parseISO(request.requestDate), 'EEEE')}
                            </small>
                          </td>
                          <td>
                            <div style={{ maxWidth: '250px' }}>{request.reason}</div>
                          </td>
                          <td>{request.estimatedHours}h</td>
                          <td>
                            {request.projectCode ? (
                              <span>
                                {request.projectCode.code}
                                <br />
                                <small className='text-muted'>{request.projectCode.name}</small>
                              </span>
                            ) : (
                              <span className='text-muted'>-</span>
                            )}
                          </td>
                          <td>
                            <Badge bg={getStatusColor(request.status)}>{request.status}</Badge>
                            {request.compOffGranted && (
                              <>
                                <br />
                                <Badge bg='info' className='mt-1'>
                                  Comp-Off: {request.compOffHours}h
                                </Badge>
                              </>
                            )}
                          </td>
                          <td>
                            {request.status === 'PENDING' ? (
                              <div className='d-flex gap-2'>
                                {/* <Button
                                  size='sm'
                                  variant='success'
                                  onClick={() => openApproveModal(request)}
                                >
                                  <i className='fas fa-check me-1'></i>
                                  Approve
                                </Button>
                                <Button
                                  size='sm'
                                  variant='danger'
                                  onClick={() => openRejectModal(request)}
                                >
                                  <i className='fas fa-times me-1'></i>
                                  Reject
                                </Button> */}
                              </div>
                            ) : request.status === 'APPROVED' && request.approvalRemarks ? (
                              <small className='text-success'>
                                <i className='fas fa-comment me-1'></i>
                                {request.approvalRemarks}
                              </small>
                            ) : request.status === 'REJECTED' && request.rejectionReason ? (
                              <small className='text-danger'>
                                <i className='fas fa-comment me-1'></i>
                                {request.rejectionReason}
                              </small>
                            ) : (
                              <span className='text-muted'>-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Approve Modal */}
      <Modal show={showApproveModal} onHide={() => setShowApproveModal(false)} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>Approve Weekend Work Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <>
              <div className='alert alert-info'>
                <h6>Request Details:</h6>
                <p className='mb-1'>
                  <strong>Employee:</strong> {selectedRequest.user?.firstName}{' '}
                  {selectedRequest.user?.lastName}
                </p>
                <p className='mb-1'>
                  <strong>Date:</strong> {format(parseISO(selectedRequest.requestDate), 'EEEE, MMMM dd, yyyy')}
                </p>
                <p className='mb-1'>
                  <strong>Hours:</strong> {selectedRequest.estimatedHours} hours
                </p>
                <p className='mb-0'>
                  <strong>Reason:</strong> {selectedRequest.reason}
                </p>
              </div>

              <Form.Group className='mb-3'>
                <Form.Label>Approval Remarks (Optional)</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={2}
                  value={approveData.approvalRemarks}
                  onChange={(e) =>
                    setApproveData({ ...approveData, approvalRemarks: e.target.value })
                  }
                  placeholder='Add any comments or instructions...'
                />
              </Form.Group>

              <hr />

              <h6 className='mb-3'>Compensatory Off (Comp-Off)</h6>

              <Form.Group className='mb-3'>
                <Form.Check
                  type='checkbox'
                  label='Grant Compensatory Off'
                  checked={approveData.compOffGranted}
                  onChange={(e) =>
                    setApproveData({ ...approveData, compOffGranted: e.target.checked })
                  }
                />
                <Form.Text className='text-muted'>
                  Check this if the employee should receive comp-off for working on weekend
                </Form.Text>
              </Form.Group>

              {approveData.compOffGranted && (
                <>
                  <Form.Group className='mb-3'>
                    <Form.Label>Comp-Off Hours</Form.Label>
                    <Form.Control
                      type='number'
                      min='0'
                      max={selectedRequest.estimatedHours}
                      value={approveData.compOffHours}
                      onChange={(e) =>
                        setApproveData({ ...approveData, compOffHours: parseInt(e.target.value) })
                      }
                    />
                    <Form.Text className='text-muted'>
                      How many hours of comp-off to grant? (Max: {selectedRequest.estimatedHours})
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className='mb-3'>
                    <Form.Label>Comp-Off Reason</Form.Label>
                    <Form.Control
                      as='textarea'
                      rows={2}
                      value={approveData.compOffReason}
                      onChange={(e) =>
                        setApproveData({ ...approveData, compOffReason: e.target.value })
                      }
                      placeholder='Reason for granting comp-off...'
                    />
                  </Form.Group>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowApproveModal(false)}>
            Cancel
          </Button>
          <Button variant='success' onClick={handleApprove}>
            <i className='fas fa-check me-2'></i>
            Approve Request
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reject Modal */}
      <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reject Weekend Work Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <>
              <div className='alert alert-warning'>
                <h6>Request Details:</h6>
                <p className='mb-1'>
                  <strong>Employee:</strong> {selectedRequest.user?.firstName}{' '}
                  {selectedRequest.user?.lastName}
                </p>
                <p className='mb-1'>
                  <strong>Date:</strong> {format(parseISO(selectedRequest.requestDate), 'EEEE, MMMM dd, yyyy')}
                </p>
                <p className='mb-0'>
                  <strong>Reason:</strong> {selectedRequest.reason}
                </p>
              </div>

              <Form.Group>
                <Form.Label>
                  Reason for Rejection <span className='text-danger'>*</span>
                </Form.Label>
                <Form.Control
                  as='textarea'
                  rows={3}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder='Explain why this request is being rejected...'
                  required
                />
                <Form.Text className='text-danger'>
                  Please provide a detailed reason (minimum 10 characters)
                </Form.Text>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowRejectModal(false)}>
            Cancel
          </Button>
          <Button variant='danger' onClick={handleReject}>
            <i className='fas fa-times me-2'></i>
            Reject Request
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default WeekendWorkApprovals

